import type { SectionARowData } from '../types'
import { SectionAViewProps } from '../types'
import { ApiCPReport } from '@ors/types/api_country-programme_records'
import { CPModel, ReportVariant } from '@ors/types/variants'

import { useMemo, useRef, useState } from 'react'

import { Alert, Checkbox, FormControlLabel } from '@mui/material'
import cx from 'classnames'
import { each, includes, union } from 'lodash'

import components from '@ors/config/Table/components'

import Table from '@ors/components/manage/Form/Table'
import Footnotes from '@ors/components/theme/Footnotes/Footnotes'

import TableDataSelector, { useTableDataSelector } from '../TableDataSelector'
import useGridOptions from './schema'

import { IoInformationCircleOutline } from 'react-icons/io5'

function getRowData(
  report: ApiCPReport,
  showOnlyReported: boolean,
  variant: ReportVariant,
): SectionARowData[] {
  let rowData: SectionARowData[] = []
  const dataByGroup: Record<string, any[]> = {}
  const groups: string[] = []

  let data = report.section_a
  if (showOnlyReported) {
    data = data.filter((item) => item.id !== 0)
  }

  each(data, (item) => {
    const group = item.group || 'Other'
    if (!dataByGroup[group]) {
      dataByGroup[group] = []
    }
    if (!includes(groups, group)) {
      groups.push(group)
    }
    dataByGroup[group].push({ ...item, group })
  })
  each(groups, (group: string) => {
    rowData = union(
      rowData,
      [{ display_name: group, group, row_id: group, rowType: 'group' }],
      dataByGroup[group],
      group === 'Annex C, Group I' && !variant.match([CPModel.V, CPModel.VI])
        ? [
            {
              display_name: 'Other',
              group,
              row_id: 'other-new_substance',
              rowType: 'control',
            },
          ]
        : [],
      [
        {
          display_name: 'Sub-total',
          group,
          row_id: `subtotal_${group}`,
          rowType: 'subtotal',
        },
      ],
    )
  })
  return rowData
}

function getPinnedRowData(rowData: SectionARowData[]): SectionARowData[] {
  return rowData.length > 0
    ? [
        {
          display_name: 'TOTAL',
          row_id: 'bottom_total',
          rowType: 'total',
          tooltip: true,
        },
      ]
    : []
}

export default function SectionAView(props: SectionAViewProps) {
  const { Comments, TableProps, emptyForm, report, showComments, variant } =
    props
  const { gridOptionsAll, gridOptionsBySector, gridOptionsBySubstanceTrade } =
    useGridOptions({
      variant: variant,
      usages: emptyForm.usage_columns?.section_a || [],
    })
  const grid = useRef<any>()
  const [showOnlyReported, setShowOnlyReported] = useState(false)
  const { setValue: setTableDataValue, value: tableDataValue } =
    useTableDataSelector()

  const rowData = getRowData(report, showOnlyReported, variant)
  const [pinnedBottomRowData] = useState(() => getPinnedRowData(rowData))

  const gridOptions = useMemo(() => {
    switch (tableDataValue) {
      case 'all':
        return gridOptionsAll
      case 'sector':
        return gridOptionsBySector
      case 'trade':
        return gridOptionsBySubstanceTrade
      default:
        return {}
    }
  }, [
    gridOptionsAll,
    gridOptionsBySector,
    gridOptionsBySubstanceTrade,
    tableDataValue,
  ])

  return (
    <>
      <Alert
        className="bg-mlfs-bannerColor"
        icon={<IoInformationCircleOutline size={24} />}
        severity="info"
      >
        <Footnotes />
      </Alert>
      <div
        className={cx('flex', {
          'justify-between': variant.match([CPModel.IV, CPModel.V, CPModel.VI]),
          'justify-end': !variant.match([CPModel.IV, CPModel.V, CPModel.VI]),
        })}
      >
        {variant.match([CPModel.IV, CPModel.V, CPModel.VI]) && (
          <TableDataSelector
            changeHandler={(_, value) => setTableDataValue(value)}
            value={tableDataValue}
          />
        )}
        <FormControlLabel
          label="Show only reported substances"
          control={
            <Checkbox
              checked={showOnlyReported}
              onChange={(event) => setShowOnlyReported(event.target.checked)}
            />
          }
        />
      </div>
      <Table
        key={tableDataValue}
        {...TableProps}
        columnDefs={gridOptions.columnDefs}
        components={components}
        defaultColDef={gridOptions.defaultColDef}
        gridRef={grid}
        headerDepth={3}
        pinnedBottomRowData={pinnedBottomRowData}
        rowData={rowData}
      />
      {showComments && <Comments section="section_a" viewOnly={false} />}
    </>
  )
}
